import * as React from 'react'
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Stack,
    Switch,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'
import {
    TIMETABLE_BLOCKS_PER_DAY,
    TIMETABLE_DAY_LABELS,
    countActiveTimetableBlocks,
    createFilledTimetableBlocks,
    normalizeTimetable
} from '../utils/timetable'

const TIME_COLUMN_WIDTH = 78
const CELL_HEIGHT = 18

const buildRows = blockMinutes => {
    const stepsPerHour = 60 / blockMinutes

    return Array.from({ length: 24 * stepsPerHour }, (_, rowIndex) => {
        const totalMinutes = rowIndex * blockMinutes
        const hour = Math.floor(totalMinutes / 60)
        const minute = totalMinutes % 60

        return {
            rowIndex,
            label: minute === 0 ?
                `${String(hour).padStart(2, '0')}:00` :
                `:${String(minute).padStart(2, '0')}`,
            isHourStart: minute === 0
        }
    })
}

const getRangeForCell = (dayIndex, rowIndex, useFiveMinuteBlocks) => {
    const blockCount = useFiveMinuteBlocks ? 1 : 3
    const start = dayIndex * TIMETABLE_BLOCKS_PER_DAY + rowIndex * blockCount

    return Array.from({ length: blockCount }, (_, offset) => start + offset)
}

const getCellState = (blocks, dayIndex, rowIndex, useFiveMinuteBlocks) => {
    const indices = getRangeForCell(dayIndex, rowIndex, useFiveMinuteBlocks)
    const activeCount = indices.reduce((count, index) => count + (blocks[index] === '1' ? 1 : 0), 0)

    if (activeCount <= 0)
        return 'off'

    if (activeCount >= indices.length)
        return 'on'

    return 'partial'
}

export default function BotTimetableDialog({
    open,
    value,
    onClose,
    onSave,
    appLanguage = 'en'
}) {
    const [draft, setDraft] = React.useState(() => normalizeTimetable(value))
    const dragStateRef = React.useRef({ active: false, value: false })
    const t = useAutoTranslate(appLanguage, [
        'Bot Timetable',
        'Select 5-minute blocks when the bot should be ACTIVE. Drag to paint, click to toggle. Grey = inactive, Green = active.',
        'Enable timetable',
        '5-minute blocks',
        'Time',
        'Clear',
        'Fill All',
        'Cancel',
        'Save',
        'blocks active'
    ])

    React.useEffect(() => {
        if (!open)
            return

        setDraft(normalizeTimetable(value))
    }, [open, value])

    React.useEffect(() => {
        if (!open)
            return undefined

        const handleMouseUp = () => {
            dragStateRef.current.active = false
        }

        window.addEventListener('mouseup', handleMouseUp)
        return () => window.removeEventListener('mouseup', handleMouseUp)
    }, [open])

    const rows = React.useMemo(
        () => buildRows(draft.useFiveMinuteBlocks ? 5 : 15),
        [draft.useFiveMinuteBlocks]
    )

    const applyValueToCell = React.useCallback((dayIndex, rowIndex, nextValue) => {
        setDraft(previous => {
            const indices = getRangeForCell(dayIndex, rowIndex, previous.useFiveMinuteBlocks)
            const nextBlocks = previous.blocks.split('')

            indices.forEach(index => {
                nextBlocks[index] = nextValue ? '1' : '0'
            })

            return {
                ...previous,
                blocks: nextBlocks.join('')
            }
        })
    }, [])

    const activeBlockCount = React.useMemo(() => countActiveTimetableBlocks(draft), [draft])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(1800px, calc(100vw - 32px))',
                    maxWidth: 'none',
                    borderRadius: 3,
                    backgroundImage: 'none',
                    backgroundColor: 'rgba(15,23,38,0.98)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
                    overflow: 'hidden'
                }
            }}
        >
            <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 3 }, userSelect: 'none' }}>
                <Stack spacing={2.25}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box>
                            <Typography variant="h4" sx={{ fontSize: { xs: '1.45rem', md: '1.9rem' } }}>
                                {t('Bot Timetable')}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.6, color: 'text.secondary', maxWidth: 980 }}>
                                {t('Select 5-minute blocks when the bot should be ACTIVE. Drag to paint, click to toggle. Grey = inactive, Green = active.')}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                                checked={draft.enabled}
                                onChange={event => setDraft(previous => ({ ...previous, enabled: event.target.checked }))}
                            />
                            <Typography variant="body1">{t('Enable timetable')}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                                checked={draft.useFiveMinuteBlocks}
                                onChange={event => setDraft(previous => ({ ...previous, useFiveMinuteBlocks: event.target.checked }))}
                            />
                            <Typography variant="body1">{t('5-minute blocks')}</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {`${activeBlockCount} ${t('blocks active')}`}
                        </Typography>
                    </Stack>

                    <Box
                        sx={{
                            borderRadius: 2.5,
                            border: '1px solid rgba(255,255,255,0.08)',
                            overflow: 'hidden',
                            backgroundColor: 'rgba(255,255,255,0.02)'
                        }}
                    >
                        <Box sx={{ overflow: 'auto', maxHeight: { xs: 420, md: 560, xl: 620 } }}>
                            <Box sx={{ minWidth: 1180 }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 3,
                                        display: 'grid',
                                        gridTemplateColumns: `${TIME_COLUMN_WIDTH}px repeat(7, minmax(140px, 1fr))`,
                                        backgroundColor: 'rgba(15, 25, 42, 0.98)',
                                        borderBottom: '1px solid rgba(255,255,255,0.08)'
                                    }}
                                >
                                    <Box sx={{ px: 1.2, py: 1.1, borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {t('Time')}
                                        </Typography>
                                    </Box>
                                    {TIMETABLE_DAY_LABELS.map(label => (
                                        <Box
                                            key={label}
                                            sx={{
                                                px: 1.2,
                                                py: 1.1,
                                                textAlign: 'center',
                                                borderRight: '1px solid rgba(255,255,255,0.08)'
                                            }}
                                        >
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                {label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {rows.map(row => (
                                    <Box
                                        key={row.rowIndex}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: `${TIME_COLUMN_WIDTH}px repeat(7, minmax(140px, 1fr))`,
                                            minHeight: CELL_HEIGHT,
                                            borderTop: row.isHourStart ? '2px solid rgba(255,255,255,0.12)' : '1px solid rgba(255,255,255,0.05)'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 0.8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                color: row.isHourStart ? 'rgba(230,237,255,0.88)' : 'text.secondary',
                                                fontSize: '0.82rem',
                                                borderRight: '1px solid rgba(255,255,255,0.08)'
                                            }}
                                        >
                                            {row.label}
                                        </Box>

                                        {TIMETABLE_DAY_LABELS.map((label, dayIndex) => {
                                            const cellState = getCellState(draft.blocks, dayIndex, row.rowIndex, draft.useFiveMinuteBlocks)
                                            const active = cellState === 'on'
                                            const partial = cellState === 'partial'

                                            return (
                                                <Box
                                                    key={`${label}-${row.rowIndex}`}
                                                    onMouseDown={event => {
                                                        event.preventDefault()
                                                        const nextValue = cellState === 'off'

                                                        dragStateRef.current = { active: true, value: nextValue }
                                                        applyValueToCell(dayIndex, row.rowIndex, nextValue)
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (!dragStateRef.current.active)
                                                            return

                                                        applyValueToCell(dayIndex, row.rowIndex, dragStateRef.current.value)
                                                    }}
                                                    sx={{
                                                        minHeight: CELL_HEIGHT,
                                                        cursor: 'cell',
                                                        borderRight: '1px solid rgba(255,255,255,0.08)',
                                                        backgroundColor: active ?
                                                            'rgba(16,185,129,0.92)' :
                                                            partial ?
                                                                'rgba(16,185,129,0.34)' :
                                                                'rgba(255,255,255,0.02)',
                                                        transition: 'background-color 120ms ease'
                                                    }}
                                                />
                                            )
                                        })}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Stack direction="row" spacing={1.2}>
                            <Button
                                variant="contained"
                                onClick={() => setDraft(previous => ({ ...previous, blocks: createFilledTimetableBlocks() }))}
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                    color: 'text.primary',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Clear')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => setDraft(previous => ({ ...previous, blocks: createFilledTimetableBlocks('1') }))}
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                    color: 'text.primary',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.16)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Fill All')}
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1.2}>
                            <Button variant="outlined" onClick={onClose}>
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => onSave?.(normalizeTimetable(draft))}
                                sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                            >
                                {t('Save')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        </Dialog>
    )
}
